/**
 * One request for the same reference data asked by several parts of a page at once.
 *
 * QA 19/10 S5-11: a page load sent the same GET 2 to 4 times (sector, printers, preferences,
 * employees, daily costs, unread notifications…) because the layout, the drawers and the page
 * each load it on mount. In a burst of 20 calls the API pool saturates (L-01) and every call
 * gets slower.
 *
 * Installed as an axios adapter: identical GETs (same address, same token, same language) to the
 * reference endpoints below share one request while it runs and its answer for SHARE_MS after.
 * Each caller gets its own copy of the data (no shared mutation). Any POST / PUT / PATCH / DELETE
 * empties the shared answers, so a change is always read back fresh. Live lists (orders,
 * kitchen, deliveries, tables) are never shared: their realtime refetches must hit the API.
 * `config.shareGet === false` opts a call out.
 */
import axios from 'axios';

export const SHARE_MS = 3000;

// paths after /api/v1, query string excluded
export const SHARED_GET_PATHS = [
  /^\/account\/preferences\/?$/,
  /^\/account\/get-user-info\/?$/,
  /\/sector\/?$/,
  /\/printers\/?$/,
  /\/print\/capabilities\/?$/,
  /\/employee\/?$/,
  /\/schedules\/?$/,
  /\/daily-costs\/?$/,
  /\/stock-variance\/?$/,
  // the real address (/inventory/stock-variance/restaurant/{id}?days=): the line above never matched it (QA 21/10 O5b-02)
  /^\/inventory\/stock-variance\/restaurant\/\d+\/?$/,
  // the seating plan's floor plan, asked by its two parts on mount (QA 21/10 O5b-07); an edit (PUT) empties it
  /\/sector\/\d+\/floor-plan\/?$/,
  /\/stock-alerts\/?$/,
  // the inventory badge and the ingredients page ask the same list (QA 20/10 O5-04)
  /^\/inventory\/stock-alerts\/restaurant\/\d+\/?$/,
  // the planning's profit by time slot, asked by two parts of the page (QA 20/10 O5-04)
  /\/planning\/profit\/?$/,
  /\/notifications\/unread-count\/?$/,
  /\/hot-actions\/?$/,
  /\/hot-actions\/notifications\/?$/,
  /\/ai-tokens(\/balance)?\/?$/,
  /\/business-session\/?$/,
  /\/subscription(\/status)?\/?$/,
  /\/trial(\/status)?\/?$/,
  // the real trial address (/trial/restaurants/{id}/status): the line above never matched it,
  // so the layout and the banner each sent it on ~20 pages (QA 20/10 O5-04)
  /^\/trial\/restaurants\/\d+\/status\/?$/,
  /\/kitchen-stations\/?$/,
];

const pathOf = (url) => {
  try {
    const u = new URL(url, 'http://x');
    return u.pathname.replace(/^.*\/api\/v1(?=\/)/, '');
  } catch {
    return String(url || '').split('?')[0];
  }
};

export const isSharedGet = (config) => {
  if (!config || config.shareGet === false) return false;
  if (String(config.method || 'get').toLowerCase() !== 'get') return false;
  if (config.responseType && config.responseType !== 'json') return false;
  const path = pathOf(axios.getUri(config));
  return SHARED_GET_PATHS.some((re) => re.test(path));
};

const header = (config, name) => {
  const h = config.headers || {};
  if (typeof h.get === 'function') return h.get(name) || '';
  return h[name] || h[name.toLowerCase()] || '';
};

const keyOf = (config) => [axios.getUri(config), header(config, 'Authorization'), header(config, 'Accept-Language')].join('|');

const clone = (data) => {
  if (data == null || typeof data !== 'object') return data;
  try { return typeof structuredClone === 'function' ? structuredClone(data) : JSON.parse(JSON.stringify(data)); } catch { return data; }
};

const entries = new Map(); // key -> { promise, at, settled }

export const clearSharedGets = () => entries.clear();

/** Wrap an adapter so the reference GETs are shared; anything else passes through. */
export function sharedGetAdapter(adapter, { now = () => Date.now() } = {}) {
  return function sharedAdapter(config) {
    const method = String(config.method || 'get').toLowerCase();
    if (method !== 'get' && method !== 'head' && method !== 'options') clearSharedGets();
    if (!isSharedGet(config)) return adapter(config);
    const key = keyOf(config);
    const hit = entries.get(key);
    const t = now();
    if (hit && (!hit.settled || t - hit.at < SHARE_MS)) {
      // the raw body as received (before axios parses it): each caller parses its own copy
      return hit.promise.then(() => ({ ...hit.res, config, data: clone(hit.raw) }));
    }
    const entry = { at: t, settled: false };
    entry.promise = Promise.resolve(adapter(config)).then(
      (res) => { entry.settled = true; entry.at = now(); entry.res = res; entry.raw = clone(res.data); return res; },
      (err) => { if (entries.get(key) === entry) entries.delete(key); throw err; },
    );
    entries.set(key, entry);
    return entry.promise;
  };
}

/** Install on an axios instance (or the default one). Idempotent. */
export function attachSharedGets(instance) {
  if (instance.defaults.adapter?.__sharedGets) return instance;
  const wrapped = sharedGetAdapter(axios.getAdapter(instance.defaults.adapter));
  wrapped.__sharedGets = true;
  instance.defaults.adapter = wrapped;
  return instance;
}
